import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import ImageUploadField from '../../../components/media/ImageUploadField';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero' },
  { value: 'quote', label: 'Quote' },
  { value: 'photo', label: 'Photo' },
  { value: 'text', label: 'Text' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'video', label: 'Video' },
  { value: 'timeline', label: 'Timeline' },
];

const TIMELINE_ICONS = [
  { value: 'Church', label: 'Church' },
  { value: 'Music', label: 'Music' },
  { value: 'Camera', label: 'Camera' },
  { value: 'UtensilsCrossed', label: 'Food/Dining' },
  { value: 'Heart', label: 'Heart' },
  { value: 'Clock', label: 'Clock' },
  { value: 'MapPin', label: 'Location' },
  { value: 'Car', label: 'Transport' },
  { value: 'PartyPopper', label: 'Party' },
  { value: 'Gift', label: 'Gift' },
  { value: 'Flower2', label: 'Flower' },
  { value: 'Sparkles', label: 'Sparkles' },
  { value: 'Star', label: 'Star' },
  { value: 'Cake', label: 'Cake' },
  { value: 'Wine', label: 'Wine' },
];

const CmsStorySectionItem = ({ section, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, weddingId }) => {
  const [expanded, setExpanded] = useState(false);

  const update = (field, value) => {
    onChange(index, { ...section, [field]: value });
  };

  const renderTypeFields = () => {
    switch (section.type) {
      case 'hero':
        return (
          <div className="space-y-3">
            <Input label="Title" type="text" value={section.title || ''} onChange={(e) => update('title', e.target.value)} />
            <Input label="Subtitle" type="text" value={section.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} />
            <ImageUploadField label="Background Image URL" value={section.backgroundImage || ''} onChange={(e) => update('backgroundImage', e.target.value)} showPreview={true} weddingId={weddingId} />
            <Input label="Background Image Alt" type="text" value={section.backgroundImageAlt || ''} onChange={(e) => update('backgroundImageAlt', e.target.value)} />
          </div>
        );

      case 'quote':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Quote</label>
              <textarea
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[80px]"
                value={section.quote || ''}
                onChange={(e) => update('quote', e.target.value)}
                placeholder="Enter the quote text..."
              />
            </div>
            <Input label="Author" type="text" value={section.author || ''} onChange={(e) => update('author', e.target.value)} />
          </div>
        );

      case 'photo':
        return (
          <div className="space-y-3">
            <ImageUploadField label="Image URL" value={section.image || ''} onChange={(e) => update('image', e.target.value)} showPreview={true} weddingId={weddingId} />
            <Input label="Image Alt Text" type="text" value={section.imageAlt || ''} onChange={(e) => update('imageAlt', e.target.value)} />
            <Input label="Caption" type="text" value={section.caption || ''} onChange={(e) => update('caption', e.target.value)} />
          </div>
        );

      case 'text':
        return (
          <div className="space-y-3">
            <Input label="Title" type="text" value={section.title || ''} onChange={(e) => update('title', e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Content</label>
              <textarea
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[120px]"
                value={section.content || ''}
                onChange={(e) => update('content', e.target.value)}
                placeholder="Enter the text content... (use \n for line breaks)"
              />
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-3">
            <Input label="Gallery Title" type="text" value={section.title || ''} onChange={(e) => update('title', e.target.value)} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Images</label>
                <Button variant="outline" size="sm" onClick={() => {
                  const images = [...(section.images || []), { url: '', alt: '' }];
                  update('images', images);
                }} iconName="Plus" iconPosition="left">Add Image</Button>
              </div>
              {(section.images || []).map((img, imgIdx) => (
                <div key={imgIdx} className="flex items-start gap-2 mb-3 p-3 bg-muted/30 rounded-lg">
                  <div className="flex-1 space-y-2">
                    <ImageUploadField
                      label={`Image ${imgIdx + 1} URL`}
                      value={img.url || ''}
                      onChange={(e) => {
                        const images = [...section.images];
                        images[imgIdx] = { ...images[imgIdx], url: e.target.value };
                        update('images', images);
                      }}
                      showPreview={true}
                      placeholder="Enter image URL or upload"
                      weddingId={weddingId}
                    />
                    <Input type="text" label="Alt text" placeholder="Image description" value={img.alt || ''} onChange={(e) => {
                      const images = [...section.images];
                      images[imgIdx] = { ...images[imgIdx], alt: e.target.value };
                      update('images', images);
                    }} />
                  </div>
                  <button
                    onClick={() => {
                      const images = section.images.filter((_, i) => i !== imgIdx);
                      update('images', images);
                    }}
                    className="p-2 text-muted-foreground hover:text-error transition-romantic"
                    title="Remove image"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3">
            <div>
              <Input
                label="Video Embed URL"
                type="text"
                placeholder="Paste YouTube, Instagram, or TikTok URL here"
                value={section.videoUrl || ''}
                onChange={(e) => update('videoUrl', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supported: YouTube videos, Instagram Reels/Posts, TikTok videos
              </p>
            </div>
            <Input label="Video Title" type="text" value={section.videoTitle || ''} onChange={(e) => update('videoTitle', e.target.value)} />
            <div className="p-3 bg-muted/20 rounded-lg border border-border">
              <p className="text-xs font-medium text-foreground mb-2">How to get embed URLs:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• <strong>YouTube:</strong> Share → Embed → Copy URL from iframe src</li>
                <li>• <strong>Instagram:</strong> Post/Reel URL → Add "/embed" at the end</li>
                <li>• <strong>TikTok:</strong> Share → Embed → Copy URL from iframe src</li>
              </ul>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="space-y-3">
            <Input label="Timeline Title" type="text" value={section.title || ''} onChange={(e) => update('title', e.target.value)} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Events</label>
                <Button variant="outline" size="sm" onClick={() => {
                  const events = [...(section.events || []), { icon: 'Star', title: '', time: '', description: '' }];
                  update('events', events);
                }} iconName="Plus" iconPosition="left">Add Event</Button>
              </div>
              {(section.events || []).map((evt, evtIdx) => (
                <div key={evtIdx} className="p-3 bg-muted/30 rounded-lg mb-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Event {evtIdx + 1}</span>
                    <button
                      onClick={() => {
                        const events = section.events.filter((_, i) => i !== evtIdx);
                        update('events', events);
                      }}
                      className="p-1 text-muted-foreground hover:text-error transition-romantic"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Icon</label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg shrink-0">
                          <Icon name={evt.icon || 'Star'} size={20} color="var(--color-primary)" />
                        </div>
                        <select
                          className="flex-1 h-10 px-3 bg-background border border-border rounded-lg text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          value={evt.icon || 'Star'}
                          onChange={(e) => {
                            const events = [...section.events];
                            events[evtIdx] = { ...events[evtIdx], icon: e.target.value };
                            update('events', events);
                          }}
                        >
                          {TIMELINE_ICONS.map((iconOption) => (
                            <option key={iconOption.value} value={iconOption.value}>
                              {iconOption.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Input type="text" placeholder="Title" value={evt.title || ''} onChange={(e) => {
                      const events = [...section.events];
                      events[evtIdx] = { ...events[evtIdx], title: e.target.value };
                      update('events', events);
                    }} />
                    <Input type="text" placeholder="Time" value={evt.time || ''} onChange={(e) => {
                      const events = [...section.events];
                      events[evtIdx] = { ...events[evtIdx], time: e.target.value };
                      update('events', events);
                    }} />
                  </div>
                  <Input type="text" placeholder="Description" value={evt.description || ''} onChange={(e) => {
                    const events = [...section.events];
                    events[evtIdx] = { ...events[evtIdx], description: e.target.value };
                    update('events', events);
                  }} />
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <p className="text-sm text-muted-foreground">Unknown section type: {section.type}</p>;
    }
  };

  const typeLabel = SECTION_TYPES.find(t => t.value === section.type)?.label || section.type;

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 transition-romantic"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex flex-col gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={isFirst}
            className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-romantic"
          >
            <Icon name="ChevronUp" size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={isLast}
            className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-romantic"
          >
            <Icon name="ChevronDown" size={14} />
          </button>
        </div>

        <span className="text-xs font-mono text-muted-foreground w-6">#{section.id}</span>
        <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">{typeLabel}</span>
        <span className="flex-1 text-sm text-foreground truncate">
          {section.title || section.quote?.slice(0, 50) || section.caption || section.videoTitle || '(untitled)'}
        </span>

        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="p-1.5 text-muted-foreground hover:text-error transition-romantic"
        >
          <Icon name="Trash2" size={16} />
        </button>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground" />
      </div>

      {expanded && (
        <div className="p-4 border-t border-border space-y-3">
          <Select
            label="Section Type"
            options={SECTION_TYPES}
            value={section.type || 'text'}
            onChange={(value) => update('type', value)}
          />
          {renderTypeFields()}
        </div>
      )}
    </div>
  );
};

export default CmsStorySectionItem;
